import React from "react";
import { View, StyleSheet, Platform, Text } from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { useTheme } from "../../theme";
import CrossPlatformDropdown from "../../components/CrossPlatformDropdown";
import { DropDownOption } from "../../components/LHFilter";
import { setSelectedFilters } from "../../store/slices/observationSlice";
import { FilterOptions } from "../../types/observation";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";

// Define types for our components
type FilterType = "employee" | "role" | "observationType" | "shift";

interface ObservationFiltersProps {
  dropDownfilterOptions: FilterOptions;
  filters: {
    employee: string;
    role: string;
    observationType: string;
    shift: string;
  };
  setFilters: React.Dispatch<
    React.SetStateAction<{
      employee: string;
      role: string;
      observationType: string;
      shift: string;
    }>
  >;
  showValidation: boolean;
}

const ObservationFilters: React.FC<ObservationFiltersProps> = ({
  dropDownfilterOptions,
  filters,
  setFilters,
  showValidation,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const labelColor = isDark ? '#FFFFFF' : '#343A40';
  const requiredColor = isDark ? '#FFFFFF' : '#6C757D';
  const infoIconColor = isDark ? '#FFFFFF' : '#6C757D';

  // Get data from Redux state for employees
  const { employees } = useSelector((state: any) => state.appData);
  const dispatch = useDispatch();

  const renderFilterDropdowns = () => {
    const filterTypes: FilterType[] = [
      "employee",
      "role",
      "observationType",
      "shift",
    ];
    const placeholders = {
      employee: "Employee",
      role: "Role",
      observationType: "Observation Type",
      shift: "Shift",
    };

    return (
      <View>
        <View style={styles.responsiveDropdownsContainer}>
          {filterTypes.map((type) => (
            <View key={type} style={styles.responsiveDropdown}>
              {/* Custom label row so we can show info icon and right-aligned required text like the design */}
              <View style={styles.labelRow}>
                <View style={styles.labelLeft}>
                  <Text style={[styles.fieldLabel, { color: labelColor }]}>
                    {type === "observationType"
                      ? "Observation Type"
                      : type.charAt(0).toUpperCase() + type.slice(1)}
                  </Text>
                  <MaterialIcons
                    name="info-outline"
                    size={16}
                    color={infoIconColor}
                    style={styles.infoIcon}
                  />
                </View>
                <Text style={[styles.requiredText, { color: requiredColor }]}>*Required</Text>
              </View>

              <CrossPlatformDropdown
                childList={
                  dropDownfilterOptions[type][0].options as DropDownOption[]
                }
                // Hide internal label so we render our custom one above
                label={""}
                placeholder={placeholders[type]}
                selection="single"
                selectedValue={null}
                setSelectedValue={(value: any) => {
                  // Handle both single and array values
                  const selectedItem = Array.isArray(value) ? value[0] : value;

                  setFilters((prev) => ({
                    ...prev,
                    [type]: selectedItem?.displayText || "",
                  }));

                  // Dispatch Redux action with appropriate data based on filter type
                  if (type === "employee" && selectedItem) {
                    // Find the full employee data from the employees.data array
                    const employeeData = employees?.data?.find(
                      (emp: any) =>
                        String(emp.Id || emp.GPID) === selectedItem.id
                    );

                    if (employeeData) {
                      // Dispatch with all employee fields
                      dispatch(
                        setSelectedFilters({
                          [type]: selectedItem.displayText || "",
                          employeeFirstName: employeeData.First_Name || "",
                          employeeLastName: employeeData.Last_Name || "",
                          employeeGpid: employeeData.GPID || "",
                        })
                      );
                    } else {
                      // Fallback if employee data is not found
                      dispatch(
                        setSelectedFilters({
                          [type]: selectedItem.displayText || "",
                        })
                      );
                    }
                  } else {
                    // For other filter types, just dispatch the displayText
                    dispatch(
                      setSelectedFilters({
                        [type]: selectedItem?.displayText || "",
                      })
                    );
                  }
                }}
                size="small"
                isClearable
                isRequired
                helperText={
                  showValidation && filters[type] === ""
                    ? "This field is required"
                    : ""
                }
                feedbackType={
                  showValidation && filters[type] === "" ? "error" : "info"
                }
              />
            </View>
          ))}
        </View>
      </View>
    );
  };

  return renderFilterDropdowns();
};

const styles = StyleSheet.create({
  responsiveDropdownsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    flex: 1,
    marginRight: 16,
  },
  responsiveDropdown: {
    width: Platform.OS === "web" ? "48%" : "100%",
    marginBottom: 12,
  },
  labelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  labelLeft: {
    flexDirection: "row",
    alignItems: "center",
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#343A40",
    marginRight: 6,
  },
  infoIcon: {
    marginTop: 2,
  },
  requiredText: {
    color: "#6C757D",
    fontSize: 12,
  },
});

export default ObservationFilters;
